import React, { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, ArrowUpRight, X, Send, Trophy } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { API, useAuth } from "../App";
import { toast } from "sonner";

/**
 * Surfaces recent positive Win Surges (>+25% WoW) the same way
 * PerformanceAlertTile surfaces regressions. Inline debrief flow:
 *  - The rep who surged sees a "Tell us what worked" CTA → opens modal
 *  - Leaders see a list of surges (with debrief status) and can promote
 *    submitted debriefs to feed the AI Adaptive Nurture Journey
 */
const WinSurgeTile = () => {
  const { user } = useAuth();
  const isLeader = !!user && [
    "admin", "ceo", "sales_head", "hod_sales", "marketing_manager",
  ].includes(user.role);

  const [surges, setSurges] = useState([]);
  const [debriefs, setDebriefs] = useState({}); // surge_id -> debrief
  const [loading, setLoading] = useState(true);
  const [openDebrief, setOpenDebrief] = useState(null); // surge being filled in
  const [dismissed, setDismissed] = useState(false);

  const fetchSurges = async () => {
    try {
      const sr = await axios.get(`${API}/win-surges?days=14`);
      setSurges(sr.data || []);
      // Pull debriefs to render their states inline.
      if (isLeader || (sr.data || []).some(s => s.kind === "rep" && s.subject_id === user?.user_id)) {
        const dr = await axios.get(`${API}/win-debriefs?days=30`);
        const map = {};
        (dr.data || []).forEach(d => { map[d.surge_id] = d; });
        setDebriefs(map);
      }
    } catch (e) {
      // Surface in dev console — tile stays silent in UI but ops can spot 5xx.
      console.warn("Win surges fetch failed:", e?.response?.status || e?.message);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSurges(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading || dismissed || surges.length === 0) return null;

  // Restore dismissal state for the entire tile (per user, per latest surge).
  const latest = surges[0];
  const dismissKey = `winsurge_dismissed_${latest.surge_id}`;
  if (typeof window !== "undefined" && sessionStorage.getItem(dismissKey) === "1") return null;

  const myAwaitingSurge = surges.find(
    s => s.kind === "rep" && s.subject_id === user?.user_id
         && s.debrief_status === "awaiting"
  );

  return (
    <>
      <Card
        className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border-emerald-200"
        data-testid="win-surge-tile"
      >
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-emerald-700" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-emerald-900 font-['Manrope']">
                    Win surge{surges.length > 1 ? `s` : ""} this week
                  </span>
                  <span className="text-xs text-emerald-700/80">
                    {surges.length} signal{surges.length > 1 ? "s" : ""}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-0.5">
                  {isLeader
                    ? "Reps and channels posting big lifts. Capture what worked, promote to AI."
                    : "Your performance is up — tell us what worked so the team can learn from it."}
                </p>
              </div>
            </div>
            <Button
              size="sm" variant="ghost"
              onClick={() => {
                sessionStorage.setItem(dismissKey, "1");
                setDismissed(true);
              }}
              className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
              data-testid="win-surge-dismiss"
            ><X className="w-4 h-4" /></Button>
          </div>

          <div className="space-y-2">
            {surges.slice(0, 5).map(s => {
              const d = debriefs[s.surge_id];
              const isMine = s.kind === "rep" && s.subject_id === user?.user_id;
              return (
                <div
                  key={s.surge_id}
                  className="flex items-center justify-between bg-white/60 rounded-md border border-emerald-100 px-3 py-2"
                  data-testid={`win-surge-row-${s.surge_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-800">
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 mr-2">
                        {s.kind}
                      </span>
                      <span className="font-medium">{s.subject_name}</span>
                      <span className="ml-2 inline-flex items-center text-emerald-700 font-mono">
                        <ArrowUpRight className="w-3.5 h-3.5" /> +{s.lift_pct}%
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.current} vs {s.prior} prior week · {s.metric}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {s.kind === "rep" && d && d.status === "promoted" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <Sparkles className="w-3 h-3 inline mr-0.5" />Feeding AI
                      </span>
                    )}
                    {s.kind === "rep" && d && d.status === "submitted" && (
                      <>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                          Awaiting promote
                        </span>
                        {isLeader && (
                          <Button
                            size="sm" variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/win-debriefs/${d.debrief_id}/promote`);
                                toast.success("Promoted — will seed AI nurture journeys");
                                fetchSurges();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Promote failed");
                              }
                            }}
                            data-testid={`promote-debrief-${d.debrief_id}`}
                          >Promote</Button>
                        )}
                      </>
                    )}
                    {isMine && !d && s.debrief_status === "awaiting" && (
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => setOpenDebrief(s)}
                        data-testid={`open-debrief-${s.surge_id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1" />Tell us what worked
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {myAwaitingSurge && !openDebrief && (
            <p className="text-[11px] text-emerald-800 italic">
              💡 Submitting your debrief takes ~1 minute. Promoted debriefs become AI seed material for the whole team.
            </p>
          )}
        </CardContent>
      </Card>

      <DebriefModal
        surge={openDebrief}
        onClose={() => setOpenDebrief(null)}
        onSaved={() => { setOpenDebrief(null); fetchSurges(); }}
      />
    </>
  );
};

const DebriefModal = ({ surge, onClose, onSaved }) => {
  const [whatWorked, setWhatWorked] = useState("");
  const [audience, setAudience] = useState("");
  const [objection, setObjection] = useState("");
  const [scripts, setScripts] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (surge) { setWhatWorked(""); setAudience(""); setObjection(""); setScripts(""); }
  }, [surge]);

  const handleSubmit = async () => {
    if (!surge) return;
    if (whatWorked.trim().length < 10) {
      toast.error("Tell us a bit more about what worked (min 10 chars)");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/win-debriefs`, {
        surge_id: surge.surge_id,
        what_worked: whatWorked.trim(),
        audience_notes: audience.trim() || null,
        key_objection: objection.trim() || null,
        key_scripts: scripts
          .split("\n")
          .map(s => s.trim())
          .filter(s => s.length > 0)
          .slice(0, 5),
      });
      toast.success("Debrief saved — your win is now visible to leaders.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save debrief");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!surge} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="debrief-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-600" />
            Tell us what worked
          </DialogTitle>
          {surge && (
            <p className="text-xs text-slate-500">
              Your bookings: {surge.current} this week vs {surge.prior} prior (+{surge.lift_pct}%)
            </p>
          )}
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>What did you do differently this week? <span className="text-rose-600">*</span></Label>
            <Textarea
              value={whatWorked}
              onChange={(e) => setWhatWorked(e.target.value)}
              rows={5}
              placeholder="e.g. I stopped opening with the price and led with the floor plan. Buyers warmed up much faster."
              data-testid="debrief-what-worked"
            />
          </div>
          <div>
            <Label>Buyer audience that responded best <span className="text-slate-400 text-xs">(optional)</span></Label>
            <Input
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. families with young kids, mid-30s, looking for 3BHK"
              data-testid="debrief-audience"
            />
          </div>
          <div>
            <Label>An objection you handled well <span className="text-slate-400 text-xs">(optional)</span></Label>
            <Input
              value={objection}
              onChange={(e) => setObjection(e.target.value)}
              placeholder="e.g. EMI worries — I showed the rental yield comparison"
              data-testid="debrief-objection"
            />
          </div>
          <div>
            <Label>Key scripts that landed <span className="text-slate-400 text-xs">(one per line, up to 5)</span></Label>
            <Textarea
              value={scripts}
              onChange={(e) => setScripts(e.target.value)}
              rows={3}
              placeholder={`e.g. "Let me show you the actual unit you'll move into, not the model flat..."`}
              data-testid="debrief-scripts"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || whatWorked.trim().length < 10}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="debrief-submit"
            >
              <Send className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save debrief"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WinSurgeTile;
